import React, { useState } from "react"
import { useTypedSelector, useTypedDispatch, RootState } from "../../../app/store"
import { ChoiceType } from "../../simulation-decisions/types"
import { setActiveChoiceType } from "../../simulation-decisions/decisionsSlice"
import DecisionsTreeAndSearch from "../../simulation-decisions/components/decisions-tree-and-search"

const TeamActionsCenter: React.FC = () => {
  const dispatch = useTypedDispatch()
  const [activeAction, setActiveAction] = useState<ChoiceType | null>(null)

  const { currentRoom } = useTypedSelector((state: RootState) => state.collaborationSlice)

  const handleActionSelect = (actionType: ChoiceType) => {
    setActiveAction(actionType)
    // This is the key missing piece - we need to set the activeChoiceType in the decisions slice
    dispatch(setActiveChoiceType(actionType))
  }

  const handleBackToActions = () => {
    setActiveAction(null)
    // Clear the choice type when going back to actions list
    dispatch(setActiveChoiceType(null))
  }

  const getActionTitle = (actionType: ChoiceType) => {
    switch (actionType) {
      case ChoiceType.decision:
        return "Propose a Decision"
      case ChoiceType.plan:
        return "Propose a Plan"
      case ChoiceType.search:
        return "Search for Information"
      default:
        return "Action"
    }
  }

  if (!currentRoom) {
    return (
      <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
        <div className="text-center p-8">
          <div className="text-gray-500">Join a collaborative room to access team actions</div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 bg-white flex flex-col h-full min-h-0">

      {/* Action Buttons - compact for better fit */}
      {!activeAction && (
        <div className="p-4 flex flex-col space-y-3 overflow-y-auto flex-1">
          {/* Propose a Decision - Main Action (Blue) */}
          <button
            onClick={() => handleActionSelect(ChoiceType.decision)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg p-4 transition-all duration-200 hover:shadow-lg group flex-shrink-0"
          >
            <div className="flex items-center space-x-3">
              <div className="bg-blue-500 rounded-lg p-2 group-hover:bg-blue-400 transition-colors">
                <span className="text-xl">⚡</span>
              </div>
              <div className="text-left">
                <h3 className="text-base font-bold">Propose a Decision</h3>
                <p className="text-blue-100 text-xs mt-0.5">Strategic decisions requiring team consensus</p>
              </div>
            </div>
          </button>

          {/* Propose a Plan - Secondary Action (White) */}
          <button
            onClick={() => handleActionSelect(ChoiceType.plan)}
            className="w-full bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-300 hover:border-gray-400 rounded-lg p-4 transition-all duration-200 hover:shadow-lg group flex-shrink-0"
          >
            <div className="flex items-center space-x-3">
              <div className="bg-gray-100 group-hover:bg-gray-200 rounded-lg p-2 transition-colors">
                <span className="text-xl">📋</span>
              </div>
              <div className="text-left">
                <h3 className="text-base font-bold">Propose a Plan</h3>
                <p className="text-gray-500 text-xs mt-0.5">Operational plans for team execution</p>
              </div>
            </div>
          </button>

          {/* Search for Information - Secondary Action (White) */}
          <button
            onClick={() => handleActionSelect(ChoiceType.search)}
            className="w-full bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-300 hover:border-gray-400 rounded-lg p-4 transition-all duration-200 hover:shadow-lg group flex-shrink-0"
          >
            <div className="flex items-center space-x-3">
              <div className="bg-yellow-100 group-hover:bg-yellow-200 rounded-lg p-2 transition-colors">
                <span className="text-xl">🔍</span>
              </div>
              <div className="text-left">
                <h3 className="text-base font-bold">Search for Information</h3>
                <p className="text-gray-500 text-xs mt-0.5">Find additional info for decisions</p>
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Content Area for Decision Tree */}
      {activeAction && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="p-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-800">
                {getActionTitle(activeAction)}
              </h3>
              <button
                onClick={handleBackToActions}
                className="text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg border border-gray-300 text-xs font-medium hover:bg-gray-100 transition-colors"
              >
                ← Back
              </button>
            </div>
          </div>
          {/* BUG FIX: Added flex flex-col to propagate flex layout to DecisionsTreeAndSearch */}
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            <DecisionsTreeAndSearch />
          </div>
        </div>
      )}
    </div>
  )
}

export default TeamActionsCenter 